import CopyButton from "@components/buttons/CopyButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import { transactionApiServices } from "@services/apis/Transactions";
import {
  GetTravelRuleCounterpartyData,
  TravelRuleCounterpartyJson,
} from "@services/models/Transactions/GetTravelRuleCounterparty";
import React, { useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import ConstantFunctions from "../../../utils/constants_functions";

function parseCounterpartyJson(
  raw: string | TravelRuleCounterpartyJson | null | undefined,
): TravelRuleCounterpartyJson | null {
  if (raw == null) return null;
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    return raw as TravelRuleCounterpartyJson;
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as TravelRuleCounterpartyJson;
    } catch {
      return null;
    }
  }
  return null;
}

function formatHumanLabel(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  if (!v) return "--";
  return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPayloadJson(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return "";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed;
    }
  }
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return asRecord(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function readPayeeValue(
  payee: Record<string, unknown>,
  keys: string[],
): unknown {
  const entries = Object.entries(payee);
  for (const key of keys) {
    const match = entries.find(
      ([entryKey]) => entryKey.toLowerCase() === key.toLowerCase(),
    );
    if (!match || match[1] == null) continue;
    if (typeof match[1] === "string" && match[1].trim() === "") continue;
    return match[1];
  }
  return undefined;
}

function payeeValueToString(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

const MERCHANT_PAYEE_FIELDS: { keys: string[]; label: string; kind: "text" | "date" | "label" }[] =
  [
    { keys: ["entityType", "type"], label: "Type", kind: "label" },
    { keys: ["firstName"], label: "First name", kind: "text" },
    { keys: ["lastName"], label: "Last name", kind: "text" },
    { keys: ["DOB", "dob", "dateOfBirth"], label: "Date of birth", kind: "date" },
    { keys: ["placeOfBirth"], label: "Place of birth", kind: "label" },
  ];

function MerchantPayeeDetailsBox({
  details,
}: {
  details: Record<string, unknown>;
}) {
  const constantFunctions = useMemo(() => new ConstantFunctions(), []);
  const knownKeys = new Set(
    MERCHANT_PAYEE_FIELDS.flatMap((field) =>
      field.keys.map((key) => key.toLowerCase()),
    ),
  );

  const extraFields = Object.entries(details).filter(
    ([key, value]) =>
      !knownKeys.has(key.toLowerCase()) && payeeValueToString(value) !== "",
  );

  return (
    <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
      <p className="text-text-navbar-active text-[12px] font-semibold underline">
        Counterparty details (Merchant submission):
      </p>
      {MERCHANT_PAYEE_FIELDS.map((field) => {
        const raw = payeeValueToString(readPayeeValue(details, field.keys));
        let value = raw;
        if (raw && field.kind === "date") {
          value = Number.isNaN(new Date(raw).getTime())
            ? raw
            : constantFunctions.formatDateAsddmmYY(raw);
        } else if (raw && field.kind === "label") {
          value = formatHumanLabel(raw);
        }
        return (
          <LabelRow
            key={field.label}
            label={field.label}
            value={value}
            isCopy={field.kind !== "date" && value !== ""}
          />
        );
      })}
      {extraFields.map(([key, fieldValue]) => {
        const value = payeeValueToString(fieldValue);
        return (
          <LabelRow
            key={key}
            label={formatHumanLabel(
              key.replace(/([a-z])([A-Z])/g, "$1 $2"),
            )}
            value={value}
            isCopy={value !== ""}
          />
        );
      })}
    </div>
  );
}

function TransactionPayloadBox({
  transactionId,
  merchantId,
  merchantOrderId,
  source,
}: {
  transactionId: number;
  merchantId?: number;
  merchantOrderId?: string;
  source: "unified" | "cpg";
}) {
  const [loading, setLoading] = useState(true);
  const [payloadText, setPayloadText] = useState<string | null>(null);
  const [payeeDetails, setPayeeDetails] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchTransactionPayload = async () => {
      setLoading(true);
      setPayloadText(null);
      setPayeeDetails(null);
      setInfoMessage(null);
      setErrorMessage(null);
      try {
        let id = merchantOrderId?.trim() ?? "";
        if (!id && source === "cpg") {
          const details = await transactionApiServices.getCPGTransactionDetails(
            { transactionId },
          );
          id = details.data?.merchantOrderId?.trim() ?? "";
        } else if (!id) {
          const details =
            await transactionApiServices.getUnifiedTransactionDetails({
              transactionId,
              merchantId,
            });
          id = details.data?.merchantOrderId?.trim() ?? "";
        }
        if (cancelled) return;
        if (!id) {
          setInfoMessage("No transaction payload found.");
          return;
        }
        const res =
          await transactionApiServices.getTransactionPayloadCounterparty(id);
        if (cancelled) return;
        const payloadObject = asRecord(res.data?.transactionPayload);
        const payeeEntry = payloadObject
          ? Object.entries(payloadObject).find(
              ([key]) => key.toLowerCase() === "payeedetails",
            )
          : undefined;
        const payee = payeeEntry ? asRecord(payeeEntry[1]) : null;
        if (payee && Object.keys(payee).length > 0) {
          setPayeeDetails(payee);
        }

        const formatted = formatPayloadJson(res.data?.transactionPayload);
        if (formatted) {
          setPayloadText(formatted);
          return;
        }
        setInfoMessage(res.msg?.trim() || "No transaction payload found.");
      } catch (error: unknown) {
        if (cancelled) return;
        const err = error as { response?: { data?: { msg?: string } } };
        setErrorMessage(
          err?.response?.data?.msg ??
            "Unable to load transaction payload. Please try again.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTransactionPayload();
    return () => {
      cancelled = true;
    };
  }, [merchantId, merchantOrderId, source, transactionId]);

  return (
    <>
      {payeeDetails ? <MerchantPayeeDetailsBox details={payeeDetails} /> : null}
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between items-center">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Transaction Payload:
          </p>
          {payloadText ? (
            <CopyButton
              copyText={payloadText}
              copyMessage="Transaction payload copied!"
              buttonSize="24px"
              copyIconSize={12}
            />
          ) : null}
        </div>
        {loading ? (
          <Skeleton height={14} count={6} className="my-1" />
        ) : errorMessage ? (
          <p className="text-[14px] font-medium text-text-status-red">
            {errorMessage}
          </p>
        ) : infoMessage ? (
          <p className="text-[14px] font-medium text-text-subtle">
            {infoMessage}
          </p>
        ) : payloadText ? (
          <pre className="max-h-[360px] bg-bg-page-body border border-border-subtle rounded-lg p-[12px] text-[12px] leading-relaxed text-text-base overflow-auto whitespace-pre-wrap break-all font-mono">
            {payloadText}
          </pre>
        ) : null}
      </div>
    </>
  );
}

function CounterpartyDetailsSkeleton() {
  return (
    <div className="flex flex-col gap-[12px]">
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <Skeleton height={18} width={180} />
        <Skeleton height={14} count={2} className="my-1" />
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <Skeleton height={18} width={120} />
        <Skeleton height={14} count={8} className="my-1" />
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <Skeleton height={18} width={140} />
        <Skeleton height={14} count={2} className="my-1" />
      </div>
    </div>
  );
}

function CounterpartyDetailBody({
  record,
  heading,
}: {
  record: GetTravelRuleCounterpartyData;
  heading: string;
}) {
  const constantFunctions = useMemo(() => new ConstantFunctions(), []);
  const parsed = useMemo(
    () => parseCounterpartyJson(record.counterparty_json),
    [record.counterparty_json],
  );

  const typeVal = formatHumanLabel(
    record.counterparty_type ?? parsed?.type ?? "",
  );
  const firstName =
    record.counterparty_firstName?.trim() || parsed?.firstName?.trim() || "";
  const lastName =
    record.counterparty_lastName?.trim() || parsed?.lastName?.trim() || "";
  const dobRaw = record.counterparty_dob?.trim() || parsed?.dob?.trim() || "";
  const dobDisplay =
    dobRaw === ""
      ? "--"
      : Number.isNaN(new Date(dobRaw).getTime())
        ? dobRaw
        : constantFunctions.formatDateAsddmmYY(dobRaw);
  const placeOfBirth =
    record.counterparty_placeOfBirth?.trim() ||
    parsed?.placeOfBirth?.trim() ||
    "";
  const fullName = (parsed?.fullName as string | undefined)?.trim() ?? "";
  const companyName = (parsed?.companyName as string | undefined)?.trim() ?? "";
  const residenceCountry =
    (parsed?.residenceCountry as string | undefined)?.trim() ?? "";
  const relationship =
    (parsed?.relationship as string | undefined)?.trim() ?? "";
  const address = parsed?.address;
  const addressLines =
    address && typeof address === "object" && !Array.isArray(address)
      ? [address.street, address.town, address.postCode, address.country]
          .map((x) => (typeof x === "string" ? x.trim() : ""))
          .filter(Boolean)
          .join(", ")
      : "";

  return (
    <div className="flex flex-col gap-[12px]">
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Counterparty Details (Travel Rule):
        </p>
        <div className="flex flex-row justify-between items-center">
          <div className="bg-bg-page-body py-[4px] px-[12px] rounded">
            <p className="text-text-base font-bold text-[10px]">{heading}</p>
          </div>
        </div>
        <div className="border-b border-border-subtle" />
        <span className="text-[12px] font-semibold text-text-base">
          Reference
        </span>
        <LabelRow
          label="Transaction ID"
          value={
            record.transactionId != null ? String(record.transactionId) : ""
          }
          isCopy={record.transactionId != null}
        />
        <LabelRow
          label="Compliance ID"
          value={record.complianceId != null ? String(record.complianceId) : ""}
          isCopy={record.complianceId != null}
        />
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <span className="text-[12px] font-semibold text-text-base">
          Counterparty
        </span>
        <LabelRow label="Type" value={typeVal} />
        {fullName !== "" && (
          <LabelRow label="Legal name" value={fullName} isCopy />
        )}
        {companyName !== "" && (
          <LabelRow label="Company name" value={companyName} isCopy />
        )}
        <LabelRow
          label="First name"
          value={firstName !== "" ? firstName : ""}
          isCopy={firstName !== ""}
        />
        <LabelRow
          label="Last name"
          value={lastName !== "" ? lastName : ""}
          isCopy={lastName !== ""}
        />
        <LabelRow label="Date of birth" value={dobDisplay} />
        <LabelRow
          label="Place of birth"
          value={placeOfBirth !== "" ? placeOfBirth : ""}
          isCopy={placeOfBirth !== ""}
        />
        {residenceCountry !== "" && (
          <LabelRow
            label="Country of residence"
            value={residenceCountry}
            isCopy
          />
        )}
        {relationship !== "" && (
          <LabelRow
            label="Relationship to transfer"
            value={relationship}
            isCopy
          />
        )}
        {addressLines !== "" && (
          <LabelRow label="Address" value={addressLines} isCopy />
        )}
      </div>
    </div>
  );
}

interface UnifiedTransactionCounterpartyDetailsProps {
  transactionId: number;
  ledger?: string;
  merchantId?: number;
  merchantOrderId?: string;
  source: "unified" | "cpg";
}

const UnifiedTransactionCounterpartyDetails: React.FC<
  UnifiedTransactionCounterpartyDetailsProps
> = ({ transactionId, ledger, merchantId, merchantOrderId, source }) => {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<GetTravelRuleCounterpartyData | null>(
    null,
  );
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const heading =
    ledger === "CR"
      ? "Originator details"
      : ledger != null
        ? "Beneficiary details"
        : "Counterparty details";

  useEffect(() => {
    let cancelled = false;

    const fetchTravelRuleCounterparty = async () => {
      setLoading(true);
      setRecord(null);
      setInfoMessage(null);
      setErrorMessage(null);
      try {
        const res =
          await transactionApiServices.getTravelRuleCounterparty(transactionId);
        if (cancelled) return;
        if (res.data) {
          setRecord(res.data);
          return;
        }
        setInfoMessage(res.msg?.trim() || "No travel rule request found.");
      } catch (error: unknown) {
        if (cancelled) return;
        const err = error as { response?: { data?: { msg?: string } } };
        setErrorMessage(
          err?.response?.data?.msg ??
            "Unable to load counterparty details. Please try again.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTravelRuleCounterparty();
    return () => {
      cancelled = true;
    };
  }, [transactionId]);

  return (
    <div className="flex flex-col gap-[12px] mt-[16px]">
      {loading ? (
        <CounterpartyDetailsSkeleton />
      ) : errorMessage ? (
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Counterparty Details (Travel Rule):
          </p>
          <p className="text-[14px] font-medium text-text-status-red">
            {errorMessage}
          </p>
        </div>
      ) : infoMessage ? (
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Counterparty Details (Travel Rule):
          </p>
          <p className="text-[14px] font-medium text-text-subtle">
            {infoMessage}
          </p>
        </div>
      ) : record ? (
        <CounterpartyDetailBody record={record} heading={heading} />
      ) : null}
      <TransactionPayloadBox
        transactionId={transactionId}
        merchantId={merchantId}
        merchantOrderId={merchantOrderId}
        source={source}
      />
    </div>
  );
};

export default UnifiedTransactionCounterpartyDetails;
